
class DSProgressBar extends HTMLElement {
    constructor() {
        super();
        this.STORAGE_KEY = 'active_campaign';
        this.SHIPPING_STORAGE_KEY = 'ds_shipping_status';
        this.activeCampaign = null;
        this.currentCartValue = 0;
        this.activeStepIndex = -1;
        this.steps = [];
    }
    connectedCallback() {
        // Wait for Liquid Ajax Cart to initialize
        if (window.liquidAjaxCart && window.liquidAjaxCart.cart) {
            this.init();
        } else {
            document.addEventListener(
                'liquid-ajax-cart:init',
                () => {
                    this.init();
                },
                { once: true }
            );
        }
        this.setupMutationObserver();
        this.setupCartListener();
        // Setup expandable message toggle via event delegation (survives DOM replacements)
        this._expanded = sessionStorage.getItem('ds-progress-bar-expanded') === 'true';
        this.addEventListener('click', (e) => {
            if (!e.target.closest('.ds-progress-bar__message-toggle')) return;
            const details = this.querySelector('.ds-progress-bar__message-details');
            if (!details || !details.innerHTML.trim()) return;
            this._expanded = !this._expanded;
            sessionStorage.setItem('ds-progress-bar-expanded', String(this._expanded));
            const toggle = this.querySelector('.ds-progress-bar__message-toggle');
            toggle.setAttribute('aria-expanded', String(this._expanded));
            details.hidden = !this._expanded;
        });
    }
    init() {
        // Read active campaign from localStorage
        this.activeCampaign = this.getActiveCampaign();
        if (!this.activeCampaign) {
            this.hidden = true;
            return;
        }

        // Extract steps that should be shown in the progress bar
        const allSteps = this.activeCampaign.steps || [];
        this.steps = allSteps.filter(step => step.show_in_progressbar === true);

        // Inject shipping step from ds_shipping_status if applicable
        this.injectShippingStep();

        if (this.steps.length === 0) {
            // Check if shipping tariffs exist
            const shippingTariffs = this.activeCampaign.shipping_tariffs || [];

            if (shippingTariffs.length === 0) {
                this.hidden = true;
                return;
            }

            // Find the right tariff by filtering on country_code
            const currentCountry = this.activeCampaign.request_country;
            const matchingTariff = shippingTariffs.find(tariff => tariff.country_code === currentCountry);

            if (!matchingTariff || !matchingTariff.free_from) {
                this.hidden = true;
                return;
            }

            // Use the shipping tariff free_from as a step
            const { label: freeShippingLabel, reward: freeShippingReward } = this.getFreeShippingCopy();
            this.steps = [{
                free_from_amount: matchingTariff.free_from,
                show_in_progressbar: true,
                title: freeShippingLabel,
                incentive_text: freeShippingReward
            }];
        }

        // Get current cart value
        this.updateCartValue();

        // Initial render
        this.render();
        this.hidden = false;
    }
    getShippingStatus() {
        try {
            const stored = localStorage.getItem(this.SHIPPING_STORAGE_KEY);
            return stored ? JSON.parse(stored) : null;
        } catch (e) {
            return null;
        }
    }
    getFreeShippingCopy() {
        // Keep the former data attribute as a fallback for cached or older section markup.
        const legacyText = this.dataset.freeShippingText;
        const label = this.dataset.freeShippingLabel || legacyText || 'Free shipping';
        const reward = this.dataset.freeShippingReward || legacyText || label;

        return { label, reward };
    }
    injectShippingStep() {
        const shippingStatus = this.getShippingStatus();

        // Try shipping status first, fall back to campaign shipping tariffs
        let freeFromAmount = shippingStatus?.free_from;
        if (!freeFromAmount) {
            const tariffs = this.activeCampaign?.shipping_tariffs || [];
            const country = this.activeCampaign?.request_country;
            const tariff = tariffs.find(t => t.country_code === country);
            if (tariff?.free_from) {
                freeFromAmount = tariff.free_from;
            }
        }

        if (!freeFromAmount) return;

        // Skip if there's already a shipping-type step
        const hasShippingStep = this.steps.some(step =>
            step.title?.toLowerCase().includes('versand') ||
            step.title?.toLowerCase().includes('shipping') ||
            step.incentive_text?.toLowerCase().includes('versand') ||
            step.incentive_text?.toLowerCase().includes('shipping')
        );
        if (hasShippingStep) return;

        const { label: freeShippingLabel, reward: freeShippingReward } = this.getFreeShippingCopy();
        this.steps.push({
            free_from_amount: freeFromAmount,
            show_in_progressbar: true,
            title: freeShippingLabel,
            incentive_text: freeShippingReward,
        });
    }
    getActiveCampaign() {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            return stored ? JSON.parse(stored) : null;
        } catch (error) {
            console.error('Error reading active campaign:', error);
            return null;
        }
    }
    updateCartValue() {
        if (window.liquidAjaxCart && window.liquidAjaxCart.cart) {
            const cart = window.liquidAjaxCart.cart;
            // Verwaltete Geschenk-Zeilen (_custom_gift_step) aus der Schwellen-Basis
            // herausrechnen — gleiche Regel wie freeItemsMutation (ds-cart-ajax.js).
            // Im Normalzustand kosten sie 0 € (kein Effekt); fällt der Shopify-Rabatt
            // weg, würde die voll bezahlte Ex-Geschenk-Zeile die Balken sonst auf
            // „erreicht" halten, während die Mutation das Geschenk gerade entfernt.
            const giftLinesTotal = (cart.items || []).reduce(
                (sum, item) =>
                    item.properties && item.properties._custom_gift_step
                        ? sum + item.final_line_price
                        : sum,
                0
            );
            this.currentCartValue = (cart.total_price - giftLinesTotal) / 100;
            this.cartSubtotalValue =
                ((cart.items_subtotal_price || cart.total_price) - giftLinesTotal) / 100;
        }
    }
    isShippingStep(step) {
        return step.title?.toLowerCase().includes('versand') ||
            step.title?.toLowerCase().includes('shipping') ||
            step.incentive_text?.toLowerCase().includes('versand') ||
            step.incentive_text?.toLowerCase().includes('shipping');
    }
    getCartValueForStep(step) {
        // Shipping uses total_price (after discounts), all other steps use subtotal (before discounts)
        return this.isShippingStep(step) ? this.currentCartValue : this.cartSubtotalValue;
    }
    calculateProgress() {
        if (this.steps.length === 0) return { progress: 0, activeStepIndex: -1 };
        // Sort steps by free_from_amount
        const sortedSteps = [...this.steps].sort((a, b) => (a.free_from_amount || 0) - (b.free_from_amount || 0));
        // Find the active step (highest step not yet reached)
        let activeStepIndex = -1;
        let nextStepAmount = 0;
        let previousStepAmount = 0;
        for (let i = 0; i < sortedSteps.length; i++) {
            const step = sortedSteps[i];
            const stepAmount = step.free_from_amount || 0;
            const cartValue = this.getCartValueForStep(step);
            if (cartValue < stepAmount) {
                activeStepIndex = i;
                nextStepAmount = stepAmount;
                previousStepAmount = i > 0 ? sortedSteps[i - 1].free_from_amount || 0 : 0;
                break;
            }
        }
        // If all steps are completed
        if (activeStepIndex === -1) {
            activeStepIndex = sortedSteps.length - 1;
            return {
                progress: 100,
                activeStepIndex,
                sortedSteps,
                isComplete: true,
            };
        }
        // Calculate progress using the appropriate cart value for the active step
        const activeStep = sortedSteps[activeStepIndex];
        const cartValueForProgress = this.getCartValueForStep(activeStep);
        const progress = Math.min(100, Math.max(0, (cartValueForProgress / nextStepAmount) * 100));
        return {
            progress,
            activeStepIndex,
            sortedSteps,
            nextStepAmount,
            previousStepAmount,
            isComplete: false,
        };
    }
    render() {
        const { progress, activeStepIndex, sortedSteps, nextStepAmount, isComplete } = this.calculateProgress();
        if (!sortedSteps) return;
        const currentStep = sortedSteps[activeStepIndex];

        // Message box stays visible when all steps are completed and shows the
        // completion message (ds_cart.progress_bar.completion_message)
        const messageBox = this.querySelector('.ds-progress-bar__message');
        if (messageBox) messageBox.style.display = '';

        // Update message
        this.updateMessage(currentStep, nextStepAmount, isComplete);

        // Render expandable product details for next step
        this.renderMessageDetails(currentStep, isComplete);

        // Update progress bar
        this.updateProgressBar(progress);

        const giftDisplayType = this.dataset.giftDisplayType || 'markers';

        if (giftDisplayType === 'list') {
            // Render list view
            this.renderGiftList(sortedSteps);
            // Clear markers if they exist
            const markersContainer = this.querySelector('.ds-progress-bar__markers');
            if (markersContainer) markersContainer.innerHTML = '';
        } else {
            // Render step icons (markers)
            this.renderStepIcons(sortedSteps, activeStepIndex, isComplete);
            // Clear list if it exists
            const listContainer = this.querySelector('.ds-upcoming-gifts-list');
            if (listContainer) listContainer.innerHTML = '';
        }

        this.activeStepIndex = activeStepIndex;
    }
    updateMessage(step, nextStepAmount, isComplete) {
        const messageEl = this.querySelector('.ds-progress-bar__text');
        if (!messageEl) return;

        if (isComplete) {
            const celebrationEmoji = this.dataset.celebrationEmoji || '🎉';

            // Build aggregated incentive text from ALL steps
            const stepDescriptions = this.steps
                .map(s => {
                    const discountLabel = this.dataset.textDiscountLabel || '% Rabatt';
                    return s.incentive_text || s.title || (s.type === 'volume_discount' ? `${s.discount_percent}${discountLabel}` : s.gift_products?.[0]?.title || '');
                })
                .filter(text => text.length > 0);

            const textAllBenefits = this.dataset.textAllBenefits || 'alle Vorteile';
            const textAnd = this.dataset.textAnd || 'und';

            let completionText;
            if (stepDescriptions.length === 0) {
                completionText = textAllBenefits;
            } else if (stepDescriptions.length === 1) {
                completionText = stepDescriptions[0];
            } else {
                const last = stepDescriptions.pop();
                completionText = stepDescriptions.join(', ') + ` ${textAnd} ` + last;
            }

            const completionTemplate = this.dataset.textCompletion || 'Du erhältst %incentive%';
            messageEl.innerHTML = `${celebrationEmoji} ${completionTemplate.replace('%incentive%', `<span class="ds-progress-bar__value">${completionText}</span>`)}`;
        } else if (step) {
            const cartValue = this.getCartValueForStep(step);
            const remaining = (nextStepAmount - cartValue).toFixed(2).replace('.', ',');
            let incentiveText = '';

            const discountLabel = this.dataset.textDiscountLabel || '% Rabatt';
            const giftDefault = this.dataset.textGiftDefault || 'Geschenk';
            incentiveText = step.incentive_text || step.title || (step.type === 'volume_discount' ? `${step.discount_percent}${discountLabel}` : step.gift_products?.[0]?.title || giftDefault);

            const onlyXTemplate = this.dataset.textOnlyXFor || 'Noch %amount% ausgeben für %reward%';
            messageEl.innerHTML = onlyXTemplate
                .replace('%amount%', `<span class="ds-progress-bar__value">${remaining} €</span>`)
                .replace('%reward%', `<strong>${incentiveText}</strong>`);
        }
    }
    renderMessageDetails(step, isComplete) {
        const detailsEl = this.querySelector('.ds-progress-bar__message-details');
        const chevron = this.querySelector('.ds-progress-bar__chevron');
        if (!detailsEl) return;

        const hasProducts = !isComplete && step?.gift_products?.length > 0;
        console.log('[progress-bar] renderMessageDetails step:', step?.id, 'gift_products:', step?.gift_products, 'hasProducts:', hasProducts);
        if (chevron) chevron.style.display = hasProducts ? '' : 'none';

        if (!hasProducts) {
            detailsEl.innerHTML = '';
            detailsEl.hidden = true;
            const toggle = this.querySelector('.ds-progress-bar__message-toggle');
            if (toggle) toggle.setAttribute('aria-expanded', 'false');
            return;
        }

        const products = step.gift_products;
        let html = '';
        products.forEach((product, i) => {
            if (i > 0) {
                html += `<div class="ds-progress-bar__product-separator">${this.dataset.textOr || 'oder'}</div>`;
            }
            const variantTitle = product.variants?.find(v => v.id === product.variant_id)?.title;
            const subtitle = variantTitle && variantTitle !== 'Default Title' ? variantTitle : '';
            html += `
                <div class="ds-progress-bar__product-preview">
                    <img src="${product.featured_image}" alt="${product.title}" class="ds-progress-bar__product-image">
                    <div class="ds-progress-bar__product-info">
                        <div class="ds-progress-bar__product-title">${product.title}</div>
                        ${subtitle ? `<div class="ds-progress-bar__product-subtitle">${subtitle}</div>` : ''}
                    </div>
                </div>
            `;
        });
        detailsEl.innerHTML = html;
        // Preserve user's expanded state across re-renders
        detailsEl.hidden = !this._expanded;
        const toggle = this.querySelector('.ds-progress-bar__message-toggle');
        if (toggle) toggle.setAttribute('aria-expanded', String(!!this._expanded));
    }
    updateProgressBar(progress) {
        const fillEl = this.querySelector('.ds-progress-bar__fill');
        if (!fillEl) return;
        // Animate progress using WAAPI
        if (window.DSCartAnimations) {
            window.DSCartAnimations.animateProgressBar(fillEl, progress);
        } else {
            fillEl.style.width = `${progress}%`;
        }
    }

    createMarkerIcon(step, iconContainer) {
        if (step.type === 'volume_discount') {
            const discountSvg = document.createElement('div');
            discountSvg.className = 'ds-progress-bar__step-icon ds-progress-bar__step-icon--discount';
            discountSvg.innerHTML = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<line x1="7" y1="7" x2="7.01" y2="7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
            iconContainer.appendChild(discountSvg);
        } else {
            const isShippingStep = step.title?.toLowerCase().includes('shipping') ||
                step.title?.toLowerCase().includes('versand') ||
                step.incentive_text?.toLowerCase().includes('shipping') ||
                step.incentive_text?.toLowerCase().includes('versand');
            if (isShippingStep) {
                const shippingSvg = document.createElement('div');
                shippingSvg.className = 'ds-progress-bar__step-icon ds-progress-bar__step-icon--shipping';
                shippingSvg.innerHTML = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
<path fill-rule="evenodd" clip-rule="evenodd" d="M16.5 6H3V17.25H3.375H4.5H4.52658C4.70854 18.5221 5.80257 19.5 7.125 19.5C8.44743 19.5 9.54146 18.5221 9.72342 17.25H15.0266C15.2085 18.5221 16.3026 19.5 17.625 19.5C18.9474 19.5 20.0415 18.5221 20.2234 17.25H21.75V12.4393L18.3107 9H16.5V6ZM16.5 10.5V14.5026C16.841 14.3406 17.2224 14.25 17.625 14.25C18.6721 14.25 19.5761 14.8631 19.9974 15.75H20.25V13.0607L17.6893 10.5H16.5ZM15 15.75V9V7.5H4.5V15.75H4.75261C5.17391 14.8631 6.07785 14.25 7.125 14.25C8.17215 14.25 9.07609 14.8631 9.49739 15.75H15ZM17.625 18C17.0037 18 16.5 17.4963 16.5 16.875C16.5 16.2537 17.0037 15.75 17.625 15.75C18.2463 15.75 18.75 16.2537 18.75 16.875C18.75 17.4963 18.2463 18 17.625 18ZM8.25 16.875C8.25 17.4963 7.74632 18 7.125 18C6.50368 18 6 17.4963 6 16.875C6 16.2537 6.50368 15.75 7.125 15.75C7.74632 15.75 8.25 16.2537 8.25 16.875Z" fill="white"/>
</svg>`;
                iconContainer.appendChild(shippingSvg);
            } else {
                const giftSvg = document.createElement('div');
                giftSvg.className = 'ds-progress-bar__step-icon';
                giftSvg.innerHTML = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M20 12V22H4V12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M22 7H2V12H22V7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M12 22V7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M12 7H7.5C6.83696 7 6.20107 6.73661 5.73223 6.26777C5.26339 5.79893 5 5.16304 5 4.5C5 3.83696 5.26339 3.20107 5.73223 2.73223C6.20107 2.26339 6.83696 2 7.5 2C11 2 12 7 12 7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M12 7H16.5C17.163 7 17.7989 6.73661 18.2678 6.26777C18.7366 5.79893 19 5.16304 19 4.5C19 3.83696 18.7366 3.20107 18.2678 2.73223C17.7989 2.26339 17.163 2 16.5 2C13 2 12 7 12 7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
                iconContainer.appendChild(giftSvg);
            }
        }
    }

    renderStepIcons(sortedSteps, activeStepIndex, isComplete) {
        const markersContainer = this.querySelector('.ds-progress-bar__markers');
        if (!markersContainer) return;

        const showSteps = this.dataset.showSteps !== 'false';
        if (!showSteps) return;

        markersContainer.innerHTML = '';

        // Determine the next step amount (= 100% of the bar)
        const nextStep = isComplete
            ? sortedSteps[sortedSteps.length - 1]
            : sortedSteps[activeStepIndex];
        if (!nextStep) return;

        const nextStepAmount = nextStep.free_from_amount || 0;
        if (nextStepAmount === 0) return;

        // Show completed steps at their proportional positions
        sortedSteps.forEach((step, index) => {
            const stepAmount = step.free_from_amount || 0;
            const cartValue = this.getCartValueForStep(step);
            // For shipping steps, use strict greater-than to match Liquid logic
            const isCompleted = this.isShippingStep(step)
                ? cartValue > stepAmount
                : cartValue >= stepAmount;

            // Skip steps beyond the next unreached step (unless all complete)
            if (!isComplete && index > activeStepIndex) return;

            // Position: completed steps proportional, next step at 100%
            const position = (index === activeStepIndex && !isComplete)
                ? 100
                : (stepAmount / nextStepAmount) * 100;

            const marker = document.createElement('div');
            marker.className = 'ds-progress-bar__step-marker';
            marker.style.left = `${position}%`;

            if (isCompleted) {
                marker.classList.add('completed');
            }

            if (step.type === 'volume_discount') {
                marker.classList.add('ds-progress-bar__step-marker--discount');
            }

            // Price label only on the next step (rightmost)
            if (index === activeStepIndex || (isComplete && index === sortedSteps.length - 1)) {
                const priceLabel = document.createElement('div');
                priceLabel.className = 'ds-progress-bar__step-price';
                const priceText = stepAmount % 1 === 0 ? stepAmount.toFixed(0) : stepAmount.toFixed(2).replace('.', ',');
                priceLabel.textContent = `${priceText} €`;
                marker.appendChild(priceLabel);
            }

            const iconContainer = document.createElement('div');
            iconContainer.className = 'ds-progress-bar__marker-icon-container';
            this.createMarkerIcon(step, iconContainer);
            marker.appendChild(iconContainer);
            markersContainer.appendChild(marker);
        });
    }
    renderGiftList(sortedSteps) {
        const listContainer = this.querySelector('.ds-upcoming-gifts-list');
        if (!listContainer) return;

        // Check if steps should be shown
        const showSteps = this.dataset.showSteps !== 'false';
        if (!showSteps) return;

        listContainer.innerHTML = '';

        // Get the templates
        const groupTemplate = document.getElementById('ds-step-gift-group-template');
        const itemTemplate = document.getElementById('ds-gift-item-template');
        if (!groupTemplate || !itemTemplate) return;

        sortedSteps.forEach((step) => {
            const stepAmount = step.free_from_amount || 0;
            const isUnlocked = this.currentCartValue >= stepAmount;
            const giftProducts = step.gift_products || [];
            const giftSelectionLimit = step.gift_selection_limit || 1;

            if (giftProducts.length === 0) return;

            // Hide non-selectable unlocked gifts (e.g. automatic gifts)
            if (isUnlocked && step.type !== 'selectable_gift') return;

            // Get cart items to check what's already added
            const cartItems = window.liquidAjaxCart.cart.items;

            // Filter out products already in cart
            const availableProducts = giftProducts.filter(giftProduct => {
                const variantIds = giftProduct.variants ? giftProduct.variants.map(v => v.id) : [giftProduct.variant_id];
                return !cartItems.some(item => variantIds.includes(item.variant_id));
            });

            if (availableProducts.length === 0) return;

            // Create step group
            const groupEl = groupTemplate.content.cloneNode(true).querySelector('.ds-step-gift-group');
            const headerEl = groupEl.querySelector('.ds-step-gift-group__header');
            const itemsContainer = groupEl.querySelector('.ds-step-gift-group__items');
            const progressFillEl = groupEl.querySelector('.ds-step-gift-group__progress-fill');

            // Set header text
            const xOfYTemplate = this.dataset.textXOfYFree || '%limit% von %total% Produkten gratis';
            const xProductsTemplate = this.dataset.textXProductsFree || '%count% Produkte gratis';
            const fromAmountTemplate = this.dataset.textFromAmount || 'Ab %amount%';

            if (availableProducts.length > 1 && giftSelectionLimit < availableProducts.length) {
                headerEl.textContent = xOfYTemplate.replace('%limit%', giftSelectionLimit).replace('%total%', availableProducts.length);
            } else if (availableProducts.length > 1) {
                headerEl.textContent = xProductsTemplate.replace('%count%', availableProducts.length);
            } else {
                headerEl.textContent = this.dataset.textGratis || 'GRATIS';
            }

            // Add threshold info for locked items
            if (!isUnlocked) {
                const fromText = fromAmountTemplate.replace('%amount%', stepAmount.toFixed(2));
                headerEl.textContent += ` - ${fromText}`;
            }

            // Set group progress bar
            let groupPercent = 0;
            if (stepAmount > 0) {
                groupPercent = Math.min(100, (this.currentCartValue / stepAmount) * 100);
            } else {
                groupPercent = 100;
            }
            progressFillEl.style.width = `${groupPercent}%`;

            // Show all gift products for this step
            availableProducts.forEach((giftProduct) => {
                // Clone the item template
                const itemEl = itemTemplate.content.cloneNode(true).querySelector('.ds-upcoming-gift-item');
                itemEl.classList.add(isUnlocked ? 'unlocked' : 'locked');

                // Set link URL from product handle
                itemEl.href = giftProduct.handle ? `/products/${giftProduct.handle}` : '#';

                // Set image
                const image = itemEl.querySelector('.ds-upcoming-gift-item__image');
                image.src = giftProduct.featured_image;
                image.alt = giftProduct.title;

                // Set status icon
                const statusIcon = itemEl.querySelector('.ds-upcoming-gift-item__status-icon');
                if (isUnlocked) {
                    statusIcon.innerHTML = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.6666 3.5L5.24992 9.91667L2.33325 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
                } else {
                    statusIcon.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 12V22H4V12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M22 7H2V12H22V7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 22V7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 7H7.5C6.83696 7 6.20107 6.73661 5.73223 6.26777C5.26339 5.79893 5 5.16304 5 4.5C5 3.83696 5.26339 3.20107 5.73223 2.73223C6.20107 2.26339 6.83696 2 7.5 2C11 2 12 7 12 7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 7H16.5C17.163 7 17.7989 6.73661 18.2678 6.26777C18.7366 5.79893 19 5.16304 19 4.5C19 3.83696 18.7366 3.20107 18.2678 2.73223C17.7989 2.26339 17.163 2 16.5 2C13 2 12 7 12 7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
                }

                // Set title in overlay
                const title = itemEl.querySelector('.ds-upcoming-gift-item__title');
                title.textContent = giftProduct.title;

                // Set price in overlay
                const originalPrice = itemEl.querySelector('.ds-upcoming-gift-item__original-price');
                originalPrice.textContent = `${giftProduct.price.toFixed(2)} €`;

                // For unlocked items, remove the price container entirely
                if (isUnlocked) {
                    const priceContainer = itemEl.querySelector('.ds-upcoming-gift-item__price');
                    priceContainer.remove();
                }

                itemsContainer.appendChild(itemEl);
            });

            listContainer.appendChild(groupEl);
        });
    }

    setupMutationObserver() {
        // Watch for changes to localStorage
        const observer = new MutationObserver(() => {
            const newCampaign = this.getActiveCampaign();
            // Check if campaign has changed
            if (JSON.stringify(newCampaign) !== JSON.stringify(this.activeCampaign)) {
                this.activeCampaign = newCampaign;
                const allSteps = newCampaign?.steps || [];
                this.steps = allSteps.filter(step => step.show_in_progressbar === true);
                this.render();
            }
        });

        // Since we can't directly observe localStorage, we'll use storage events
        window.addEventListener('storage', (e) => {
            if (e.key === this.STORAGE_KEY || e.key === this.SHIPPING_STORAGE_KEY) {
                const newCampaign = this.getActiveCampaign();
                if (JSON.stringify(newCampaign) !== JSON.stringify(this.activeCampaign) || e.key === this.SHIPPING_STORAGE_KEY) {
                    this.activeCampaign = newCampaign;
                    const allSteps = newCampaign?.steps || [];
                    this.steps = allSteps.filter(step => step.show_in_progressbar === true);
                    this.injectShippingStep();
                    this.render();
                }
            }
        });
    }
    setupCartListener() {
        // Listen to Liquid Ajax Cart updates
        document.addEventListener('liquid-ajax-cart:request-end', () => {
            this.updateCartValue();
            this.render();
        });
    }
    disconnectedCallback() {
        // Cleanup if needed
    }
}
if (!customElements.get('ds-progress-bar')) {
    customElements.define('ds-progress-bar', DSProgressBar);
}
